/**
 * H5 参会端 Feed：WS 字幕 + PTT 本地 STT uplink + 定稿 TTS
 */

import { computed, inject, onMounted, onUnmounted, ref, watch, type Ref } from 'vue'
import { message } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { readJoinSession } from './useCrV3JoinSession'
import { useCrV3H5Ws } from './useCrV3H5Ws'
import { useCrV3H5Stt } from './useCrV3H5Stt'
import { useCrV3H5Tts } from './useCrV3H5Tts'
import { crV3H5ControlsKey } from './cr-v3-h5-context'
import { MAX_DISPLAY_LINES } from '@/modules/speech-translation/constants'
import { shortLangCode, pickViewerRowLineText, resolveViewerTtsLocale } from '../adapters/live-speech-adapter'
import { resolveListenSttSupported } from '../utils/resolve-listen-stt-supported'
import type { TranslationRecordFeedApi } from './useCrV3ViewerFeed'
import type { ViewerLanguageItem } from '@/modules/speech-translation/api/viewer'
import {
  computeFloorBlocked,
  resolvePttEnabled,
  shouldStickyForceFloorBlock,
  type CrV3ActiveSpeakerPatch,
  type CrV3SpeakSessionPatch,
} from '../utils/speak-permission'
import { formatAudioBusyMessage } from './audio-busy'
import { SpeechTokenCache } from '../utils/speech-token-cache'
import { CrV3GlossaryCache } from '../utils/glossary-cache'
import { fetchRuntimeConfig } from '../api/cr-v3-viewer.api'
import { acquireMicStream, releaseMicStream } from '../utils/speech-pcm-utils'

export interface UseCrV3H5FeedOptions {
  selectedTargetCode: Ref<string>
}

export function useCrV3H5Feed(
  meetingCode: string,
  api: TranslationRecordFeedApi,
  feedOptions: UseCrV3H5FeedOptions,
) {
  const { t } = useI18n()
  const session = readJoinSession(meetingCode)
  const viewerName = session?.displayName ?? ''
  const languageCode = session?.languageCode ?? ''
  const languageCode2 = session?.languageCode2 ?? languageCode

  const h5Controls = inject(crV3H5ControlsKey, null)
  const pttHolding = h5Controls?.pttHolding ?? ref(false)
  const floorBlocked = h5Controls?.floorBlocked ?? ref(false)
  const speakAllowed = h5Controls?.speakAllowed ?? ref(false)
  const autoCaptureActive = h5Controls?.autoCaptureActive ?? ref(false)
  const listenSttUnsupported = h5Controls?.listenSttUnsupported ?? ref(false)

  const tokenCache = new SpeechTokenCache()
  const glossaryCache = new CrV3GlossaryCache()
  const sessionLanguages = ref<{ source: ViewerLanguageItem; targets: ViewerLanguageItem[] }>({
    source: { languageCode: 'zh' },
    targets: [{ languageCode: 'en' }],
  })
  const classroomWorkLangCode = ref('zh-CN')
  let glossaryPhrases: string[] = []
  let segmentationSilenceTimeoutMs: number | undefined
  let micStream: MediaStream | null = null
  let tokenPending = false
  let floorPending = false
  let speakSession: CrV3SpeakSessionPatch = {}
  let lastActiveSpeaker: CrV3ActiveSpeakerPatch = { speaking: false }
  let forceFloorBlocked = false

  const wsHolder: { ws: ReturnType<typeof useCrV3H5Ws> | null } = { ws: null }

  /** PTT 源语 = 入会监听语；同时用 selectedTargetCode 对齐展示语切换场景 */
  const pttSourceCode = computed(
    () => feedOptions.selectedTargetCode.value || languageCode || languageCode2,
  )

  function refreshListenSttGate() {
    listenSttUnsupported.value = !resolveListenSttSupported(
      pttSourceCode.value,
      sessionLanguages.value,
    )
  }

  watch(
    [() => sessionLanguages.value, pttSourceCode],
    () => {
      refreshListenSttGate()
    },
    { deep: true, immediate: true },
  )

  function recomputeSpeakGate() {
    const ws = wsHolder.ws
    if (!ws) {
      return
    }
    // D19-speak H5 修订：designated 模式下被点名（liveSpeech=true）自动拾音，无需按住；
    // 离开 designated（如切到 openDiscussion）或被取消点名时，先停止自动拾音，让新
    // 模式的按住发言/floor 从干净状态开始。必须在下面 selfStreaming 计算之前完成。
    applyDesignatedAutoCapture(ws.clientId.value)

    const selfStreaming = pttHolding.value || ws.sttSessionActive.value || tokenPending || floorPending
    speakAllowed.value = resolvePttEnabled('viewer', ws.clientId.value, speakSession, false)
    floorBlocked.value = computeFloorBlocked(
      forceFloorBlocked,
      lastActiveSpeaker,
      ws.clientId.value,
      selfStreaming,
      speakSession.speakMode,
    )
  }

  /**
   * D19-speak H5 修订：designated 模式下被点名（liveSpeech=true）自动拾音，参考 v2
   * 会议模式"授权即拾音"；离开 designated 或被取消点名时先停止，不遗留 floor 占用。
   * 仅 designated 生效，openDiscussion 仍保持按住发言（floor 抢占语义不同）。
   *
   * 风险提示：H5 的 getUserMedia 在部分内嵌浏览器（搜狗/QQ 等）历史上要求真实用户
   * 手势才能可靠弹出授权，非手势触发可能静默失败（startPtt 内部会捕获异常并回落
   * pttHolding=false）。因此这里"乐观"标记 autoCaptureActive，若尝试后 pttHolding
   * 未能置真，则复位该标记，使手动按钮保持可用，让用户可以补一次真实手势兜底。
   */
  function applyDesignatedAutoCapture(selfClientId: string) {
    const isDesignatedAndLive =
      (speakSession.speakMode || 'hostOnly') === 'designated'
      && (speakSession.participant || []).some((p) => p.id === selfClientId && p.liveSpeech)

    if (isDesignatedAndLive) {
      if (listenSttUnsupported.value) {
        // 当前监听语不支持 STT：不自动拾音，避免反复 toast
        return
      }
      if (!pttHolding.value && !autoCaptureActive.value) {
        autoCaptureActive.value = true
        void startPtt().finally(() => {
          if (!pttHolding.value) {
            autoCaptureActive.value = false
          }
        })
      }
      return
    }

    if (autoCaptureActive.value) {
      autoCaptureActive.value = false
      void stopPtt()
    }
  }

  /** 断线 / 服务端释放话权后清本地 floor 态，避免单人卡在「话权占用中」 */
  function resetLocalFloorState() {
    forceFloorBlocked = false
    lastActiveSpeaker = { speaking: false }
  }

  const tts = useCrV3H5Tts({
    requestToken: (intent, lang) => {
      const ws = wsHolder.ws
      if (!ws) {
        return Promise.resolve({ ok: false, busy: { reason: 'not_connected' } })
      }
      return ws.requestSpeechToken(intent, lang, { allowCache: true })
    },
  })

  const stt = useCrV3H5Stt({
    speakerName: viewerName,
    role: 'viewer',
    sourceLanguageCode2: languageCode2,
    getSessionLanguages: () => sessionLanguages.value,
    getSessionSourceShort: () => shortLangCode(sessionLanguages.value.source.languageCode),
    getSessionTargetShorts: () => sessionLanguages.value.targets.map((t) => t.languageCode),
    getGlossaryPhrases: () => glossaryPhrases,
    segmentationSilenceTimeoutMs,
    onUplink: (message) => wsHolder.ws?.sendUplinkLiveSpeech(message),
    onLocalPreview: (row) => {
      api.previewLive.value =
        row && !api.isPreviewPayloadEmpty(row) ? row : null
    },
    onLocalFragment: (row) => {
      api.fragments.value = [...api.fragments.value, row].slice(-MAX_DISPLAY_LINES)
      api.previewLive.value = null
    },
  })

  const ws = useCrV3H5Ws({
    meetingCode,
    viewerName,
    languageCode,
    tokenCache,
    callbacks: {
      onInit: (data) => {
        sessionLanguages.value = {
          source: data.sourceLanguage,
          targets: data.targetLanguages,
        }
        classroomWorkLangCode.value = data.classroomWorkLangCode ?? 'zh-CN'
        api.applyInit({
          fragments: data.fragments,
          preview: data.preview,
          sourceLanguage: data.sourceLanguage,
          targetLanguages: data.targetLanguages,
          subtitleStyle: data.subtitleStyle,
        })
      },
      onSubtitleStyleUpdate: (style) => {
        api.applyInit({ subtitleStyle: style })
      },
      onPreview: (row) => {
        api.previewLive.value =
          row && !api.isPreviewPayloadEmpty(row) ? row : null
      },
      onFragment: (row) => {
        api.fragments.value = [...api.fragments.value, row].slice(-MAX_DISPLAY_LINES)
        api.previewLive.value = null
        const speaker = (row.speaker || '').trim()
        if (
          ws.applyingHistory
          || pttHolding.value
          || ws.sttSessionActive.value
          || tokenPending
          || floorPending
          || stt.active.value
          || (speaker && speaker === viewerName)
        ) {
          return
        }
        const langShort = feedOptions.selectedTargetCode.value
        const text = pickViewerRowLineText(row, langShort)
        const langBcp = resolveViewerTtsLocale(langShort, sessionLanguages.value)
        void tts.speak(text, langBcp)
      },
      onHistoryCleared: () => {
        api.fragments.value = []
        api.previewLive.value = null
      },
      onDisconnected: (detail) => {
        resetLocalFloorState()
        void stopPtt()
        const codeHint =
          import.meta.env.DEV && detail.code ? `(${detail.code})` : ''
        message.warning(`连接已断开${codeHint}，正在重连…`)
      },
      onReconnected: () => {
        resetLocalFloorState()
        recomputeSpeakGate()
        // D15：重连 stop TTS，保持开关
        tts.stopAndClear(true)
        // 重连后重拉本场冻结 runtime（勿依赖会中 updateRuntimeConfig）
        void tts.loadRuntimeConfig(session?.workId)
        message.success('已重新连接')
      },
      onError: (msg) => {
        if (msg) {
          message.error(msg)
        }
      },
      onLoading: (v) => {
        api.loading.value = v
      },
      onAudioBusy: (data) => {
        tokenPending = false
        floorPending = false
        pttHolding.value = false
        // floor_occupied 时把真实占用者写回 lastActiveSpeaker：否则下面 startPtt() 里
        // "粘性拦截仅在 !lastActiveSpeaker.speaking 时才复位重试" 会因为 lastActiveSpeaker
        // 从未被 busy 响应更新过而永远成立，导致每次按住都重新走一遍 mic+token+floor
        // 协商（约 1s）才被拒绝——讨论模式下"按住秒取消，再按又秒取消"的根因（D21-floor-busy）
        if (data.reason === 'floor_occupied' && data.activeSpeaker?.clientId) {
          lastActiveSpeaker = { speaking: true, clientId: data.activeSpeaker.clientId }
        }
        forceFloorBlocked = shouldStickyForceFloorBlock(
          data.reason,
          speakSession.speakMode,
        )
        void stt.stop()
        releaseMic()
        message.warning(formatAudioBusyMessage(data))
        recomputeSpeakGate()
      },
      onAudioStopped: (data) => {
        const wasHolding =
          pttHolding.value || tokenPending || floorPending || stt.active.value || ws.sttSessionActive.value
        if (wasHolding) {
          void stopPtt()
          if (data.reason?.startsWith('floor_idle')) {
            message.warning('发言已超时结束，请重新按住说话')
          }
        }
        resetLocalFloorState()
        recomputeSpeakGate()
      },
      onActiveSpeakerChanged: (data) => {
        lastActiveSpeaker = { speaking: data.speaking, clientId: data.clientId }
        if (!data.speaking) {
          forceFloorBlocked = false
        }
        recomputeSpeakGate()
      },
      onSpeakPermissionChanged: (_allowed, sessionPatch) => {
        speakSession = sessionPatch
        if (sessionPatch.speakMode !== 'openDiscussion') {
          forceFloorBlocked = false
        }
        recomputeSpeakGate()
        if (!speakAllowed.value && pttHolding.value) {
          void stopPtt()
        }
      },
      onSessionLanguagesChanged: (langs) => {
        sessionLanguages.value = {
          source: langs.sourceLanguage,
          targets: langs.targetLanguages,
        }
        classroomWorkLangCode.value = langs.classroomWorkLangCode
        api.applyInit({
          sourceLanguage: langs.sourceLanguage,
          targetLanguages: langs.targetLanguages,
        })
      },
      onSessionSnapshot: (session) => {
        api.applySessionSnapshot?.(session)
        // D15：pause 停播保持开关；ended 关播并复位 enabled
        if (session.status === 'paused') {
          tts.stopAndClear(true)
        } else if (session.status === 'ended') {
          tts.close()
        }
      },
      onRuntimeConfigUpdate: (cfg) => {
        if (typeof cfg.ttsPlaybackRate === 'number') {
          tts.applyPlaybackRate(cfg.ttsPlaybackRate)
        }
      },
      onSourceLanguageReset: () => {
        if (
          pttHolding.value
          || stt.active.value
          || ws.sttSessionActive.value
          || tokenPending
          || floorPending
        ) {
          message.warning('课堂源语已变更，请重新按住发言')
          void stopPtt()
        }
      },
    },
  })

  wsHolder.ws = ws

  watch(feedOptions.selectedTargetCode, (code, prev) => {
    if (!prev || !code || code === prev || !ws.connected.value) {
      return
    }
    // D3：切语 stop+clear，保持开关
    tts.stopAndClear(true)
    void ws.changeListenLanguage(code)
  })

  async function ensureMic(): Promise<MediaStream | null> {
    if (micStream) {
      return micStream
    }
    try {
      micStream = await acquireMicStream()
      return micStream
    } catch (err) {
      message.error(err instanceof Error ? err.message : '无法访问麦克风')
      return null
    }
  }

  function releaseMic() {
    releaseMicStream(micStream)
    micStream = null
  }

  async function startPtt() {
    // 粘性 forceFloorBlocked 不永久挡死：允许用户再次按住重试（仅真实他人占麦时仍挡）
    if (forceFloorBlocked && !lastActiveSpeaker.speaking) {
      forceFloorBlocked = false
      recomputeSpeakGate()
    }
    refreshListenSttGate()
    if (listenSttUnsupported.value) {
      message.error(t('speechTranslation.pttLanguageNotSupported'))
      return
    }
    if (floorBlocked.value || !speakAllowed.value) {
      return
    }
    if (pttHolding.value || tokenPending || floorPending || stt.active.value) {
      return
    }
    forceFloorBlocked = false
    pttHolding.value = true
    tokenPending = true
    recomputeSpeakGate()

    const stream = await ensureMic()
    if (!stream || !pttHolding.value) {
      pttHolding.value = false
      tokenPending = false
      recomputeSpeakGate()
      return
    }

    if (!glossaryPhrases.length) {
      glossaryPhrases = await glossaryCache.load(meetingCode, languageCode)
    }

    const tokenResult = await ws.requestSpeechToken('stt', languageCode2, { allowCache: true })
    tokenPending = false
    if (!pttHolding.value) {
      releaseMic()
      recomputeSpeakGate()
      return
    }
    if (!tokenResult.ok) {
      pttHolding.value = false
      releaseMic()
      message.warning(formatAudioBusyMessage(tokenResult.busy))
      forceFloorBlocked = shouldStickyForceFloorBlock(
        tokenResult.busy.reason,
        speakSession.speakMode,
      )
      recomputeSpeakGate()
      return
    }

    floorPending = true
    recomputeSpeakGate()
    const floorResult = await ws.acquireFloor()
    floorPending = false
    if (!pttHolding.value) {
      // 松手竞态：acquire 已成功但 stopPtt 未发 audio:stop（当时 sttSessionActive 仍为 false）
      if (ws.sttSessionActive.value) {
        ws.sendAudioStop()
      }
      releaseMic()
      recomputeSpeakGate()
      return
    }
    if (!floorResult.ok) {
      pttHolding.value = false
      releaseMic()
      message.warning(formatAudioBusyMessage(floorResult.busy))
      forceFloorBlocked = shouldStickyForceFloorBlock(
        floorResult.busy.reason,
        speakSession.speakMode,
      )
      recomputeSpeakGate()
      return
    }

    ws.startPttKeepalive()

    try {
      await stt.start(tokenResult.data.token, tokenResult.data.region, stream)
    } catch (err) {
      pttHolding.value = false
      ws.stopPttKeepalive()
      ws.sendAudioStop()
      releaseMic()
      message.error(err instanceof Error ? err.message : '语音识别启动失败')
      recomputeSpeakGate()
    }
  }

  async function stopPtt() {
    // 任何 stop 路径（手动/内部复位/离开 designated）都清一致自动拾音标记，
    // UI 层的手动松开 guard 已经在调用前判断过，这里清零不会绕开该 guard（D19-speak H5）
    autoCaptureActive.value = false
    pttHolding.value = false
    ws.stopPttKeepalive()
    await stt.stop()
    if (ws.sttSessionActive.value) {
      ws.sendAudioStop()
    }
    releaseMic()
    recomputeSpeakGate()
  }

  if (h5Controls) {
    h5Controls.startPtt = startPtt
    h5Controls.stopPtt = stopPtt
    h5Controls.setTtsEnabled = (value: boolean) => {
      tts.setEnabled(value)
      h5Controls.ttsEnabled.value = tts.enabled.value
    }
    // 父视图可能已点开 TTS（audio unlock）
    if (h5Controls.ttsEnabled.value) {
      tts.setEnabled(true)
    }
  }

  onMounted(async () => {
    if (!session) {
      return
    }
    void fetchRuntimeConfig(session?.workId)
      .then((cfg) => {
        if (cfg.segmentationSilenceTimeoutMs) {
          segmentationSilenceTimeoutMs = cfg.segmentationSilenceTimeoutMs
        }
      })
      .catch(() => {
        //
      })
    void glossaryCache.load(meetingCode, languageCode).then((phrases) => {
      glossaryPhrases = phrases
    })
    await tts.loadRuntimeConfig(session?.workId)
    await ws.connect()
  })

  onUnmounted(() => {
    void stopPtt()
    ws.disconnect()
    tts.close()
  })

  return { startPtt, stopPtt, pttHolding, floorBlocked, speakAllowed }
}
